import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import ModalService from "shared/modals/ModalService";
import features from "shared/services/features";
import Permissions from "shared/services/Permissions";
import { type AIPanelType } from "shared/stores/ai";
import useUserStore from "shared/stores/user";

import useProductMenu from "./useProductMenu";

export interface AIAssistantOption {
  streamsLimitCheck?: boolean;
  disabled?: boolean;
  icon: string;
  iconClass: string;
  label: string;
  newIcon: boolean;
  panel?: AIPanelType;
  product: "monitoring" | "social" | "outreach";
  track: string;
}

export interface AIAssistantGroup {
  group: string;
  label: string;
  options: AIAssistantOption[];
}

function optionFilter(option: AIAssistantOption): boolean {
  return !option.disabled;
}

export default function useAIAssistantOptions(isMobile?: boolean) {
  const router = useRouter();
  const { currentRouteProductMenu } = useProductMenu();

  const userStore = useUserStore();
  const { currentUser, organisation } = storeToRefs(userStore);

  const groupsExpanded = ref<boolean>(false);

  const permissions = new Permissions();

  const monitoringOptions = computed<AIAssistantOption[]>(() => {
    const options: AIAssistantOption[] = [
      {
        label: getLocaleText("ai_assistant_panel.options.create_boolean_query"),
        icon: "article",
        iconClass: "tw:text-blue-700",
        panel: "AIBooleanCreatorPanel",
        newIcon: true,
        track: getLocaleText(
          "ai_assistant_panel.options.create_boolean_query",
          "en-AU"
        ),
        disabled:
          !features.has("has_ai_boolean_creator") ||
          !features.has("add_stream") ||
          currentUser.value.role === "restricted" ||
          !permissions.has("can_create_mention_stream"),
        product: "monitoring",
        streamsLimitCheck: true,
      },
      {
        label: getLocaleText(
          "ai_assistant_panel.options.summarise_mention_stream"
        ),
        icon: "list-view",
        iconClass: "tw:text-blue-700",
        panel: "AISummaryAssistantPanel",
        newIcon: true,
        track: getLocaleText(
          "ai_assistant_panel.options.summarise_mention_stream",
          "en-AU"
        ),
        disabled: !features.has("has_ai_summarisation"),
        product: "monitoring",
      },
      {
        label: getLocaleText(
          "ai_assistant_panel.options.organise_mention_stream"
        ),
        icon: "full-text",
        iconClass: "tw:text-blue-700",
        newIcon: true,
        track: getLocaleText(
          "ai_assistant_panel.options.organise_mention_stream",
          "en-AU"
        ),
        product: "monitoring",
      },
      {
        label: getLocaleText(
          "ai_assistant_panel.options.create_mention_streams"
        ),
        icon: "article",
        iconClass: "tw:text-blue-700",
        newIcon: true,
        panel: "AIMentionStreamsAssistantPanel",
        track: getLocaleText(
          "ai_assistant_panel.options.create_mention_streams",
          "en-AU"
        ),
        disabled:
          !features.has("has_ai_mention_streams_creator") ||
          !features.has("add_stream") ||
          currentUser.value.role === "restricted" ||
          !permissions.has("can_create_mention_stream"),
        product: "monitoring",
        streamsLimitCheck: true,
      },
    ];

    return options.filter(optionFilter);
  });

  const monitoringGroup = computed<AIAssistantGroup>(() => ({
    group: "media",
    label: getLocaleText("ai_assistant_panel.monitoring"),
    options: monitoringOptions.value,
  }));

  const socialOptions = computed<AIAssistantOption[]>(() => {
    const options: AIAssistantOption[] = [
      {
        label: getLocaleText("ai_assistant_panel.options.write_social_post"),
        icon: "article",
        iconClass: "tw:text-purple-500",
        newIcon: true,
        track: getLocaleText(
          "ai_assistant_panel.options.write_social_post",
          "en-AU"
        ),
        product: "social",
      },
      {
        label: getLocaleText("ai_assistant_panel.options.adjust_social_post"),
        icon: "article",
        iconClass: "tw:text-purple-500",
        newIcon: true,
        track: getLocaleText(
          "ai_assistant_panel.options.adjust_social_post",
          "en-AU"
        ),
        product: "social",
      },
      {
        label: getLocaleText("ai_assistant_panel.options.when_should_i_post"),
        icon: "pending-actions",
        iconClass: "tw:text-purple-500",
        newIcon: true,
        track: getLocaleText(
          "ai_assistant_panel.options.when_should_i_post",
          "en-AU"
        ),
        product: "social",
      },
    ];

    return options.filter(optionFilter);
  });

  const socialGroup = computed<AIAssistantGroup>(() => ({
    group: "social",
    label: getLocaleText("ai_assistant_panel.social"),
    options: socialOptions.value,
  }));

  const outreachOptions = computed<AIAssistantOption[]>(() => {
    const options: AIAssistantOption[] = [
      {
        label: getLocaleText("ai_assistant_panel.options.write_media_outreach"),
        icon: "article",
        iconClass: "tw:text-blue-700",
        newIcon: true,
        track: getLocaleText(
          "ai_assistant_panel.options.write_media_outreach",
          "en-AU"
        ),
        product: "outreach",
      },
      {
        label: getLocaleText(
          "ai_assistant_panel.options.adjust_media_outreach"
        ),
        icon: "article",
        iconClass: "tw:text-blue-700",
        newIcon: true,
        track: getLocaleText(
          "ai_assistant_panel.options.adjust_media_outreach",
          "en-AU"
        ),
        product: "outreach",
      },
      {
        label: getLocaleText("ai_assistant_panel.options.suggested_contacts"),
        disabled: !features.has("has_smart_suggested_contacts"),
        icon: "list-user",
        iconClass: "tw:text-blue-700",
        newIcon: true,
        track: getLocaleText(
          "ai_assistant_panel.options.suggested_contacts",
          "en-AU"
        ),
        product: "outreach",
        panel: "AISuggestedContactsAssistantPanel",
      },
      {
        label: getLocaleText("ai_assistant_panel.options.how_should_i_choose"),
        disabled: true,
        icon: "list-user",
        iconClass: "tw:text-blue-700",
        newIcon: true,
        track: getLocaleText(
          "ai_assistant_panel.options.how_should_i_choose",
          "en-AU"
        ),
        product: "outreach",
      },
      {
        label: getLocaleText("ai_assistant_panel.options.when_should_i_send"),
        icon: "pending-actions",
        iconClass: "tw:text-blue-700",
        newIcon: true,
        track: getLocaleText(
          "ai_assistant_panel.options.when_should_i_send",
          "en-AU"
        ),
        product: "outreach",
      },
    ];

    return options.filter(optionFilter);
  });

  const outreachGroup = computed<AIAssistantGroup>(() => ({
    group: "outreach",
    label: getLocaleText("ai_assistant_panel.outreach"),
    options: outreachOptions.value.filter((option) => !option.disabled),
  }));

  const optionGroups = computed<AIAssistantGroup[]>(() => {
    const groups: AIAssistantGroup[] = [];

    if (features.has("has_media_platform")) {
      groups.push(monitoringGroup.value);
    }

    if (
      features.has("has_social_platform") &&
      currentUser.value.social_role !== "none"
    ) {
      groups.push(socialGroup.value);
    }

    if (
      !isMobile &&
      features.has("has_outreach_2") &&
      features.hasAny(["has_outreach_ai_pitch", "has_outreach_media_releases"])
    ) {
      groups.push(outreachGroup.value);
    }

    return groups;
  });

  const routeGroup = computed<string>(() => {
    if (
      router.currentRoute.value.name === "dashboard-social" ||
      currentRouteProductMenu.value === "social"
    ) {
      return "social";
    }

    if (
      router.currentRoute.value.name === "dashboard-outreach" ||
      currentRouteProductMenu.value === "outreach"
    ) {
      return "outreach";
    }

    return "media";
  });

  const sortedOptionGroups = computed<AIAssistantGroup[]>(() => {
    const topGroup = optionGroups.value.find(
      (group) => group.group === routeGroup.value
    );

    const remainingGroups = optionGroups.value.filter(
      (group) => group.group !== routeGroup.value
    );

    if (!topGroup) return remainingGroups;

    if (groupsExpanded.value) return [topGroup, ...remainingGroups];

    return [topGroup];
  });

  const visibleGroupsAndOptions = computed<number>(() =>
    sortedOptionGroups.value.reduce(
      (accumulator, group) => accumulator + group.options.length,
      0
    )
  );

  async function hasStreamsCredits(
    option: AIAssistantOption
  ): Promise<boolean> {
    if (!option.streamsLimitCheck) return true;

    try {
      const params = {
        stream_type: "MentionStream",
      };

      const response = await $streemApiV1.get(
        `organisations/${organisation.value.id}/streams_limit_reached`,
        { params }
      );

      if (response.data.streams_limit_reached) {
        ModalService.open("StreamAllocationReachedModal", {
          props: {
            streamType: "mentionStream",
          },
        });

        return false;
      }

      return true;
    } catch (error) {
      ErrorDialog(
        getLocaleText("ai_assistant_panel.load_streams_limit_reached_error"),
        error
      );

      return false;
    }
  }

  return {
    groupsExpanded,

    monitoringGroup,
    monitoringOptions,
    optionGroups,
    outreachGroup,
    outreachOptions,
    routeGroup,
    socialGroup,
    socialOptions,
    sortedOptionGroups,
    visibleGroupsAndOptions,

    hasStreamsCredits,
  };
}
